import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStats } from 'react-instantsearch';
import c from 'classnames';

import { SearchFacets, SearchFacetRefinementCheckboxes } from '@uncinc/react-searchkit';
import { useWindowDimensions } from '@uncinc/uncinc-react-kitchen-sink';
import type { SearchFacetsProps } from '@uncinc/react-searchkit/dist/components/Facets';
import type { SearchFacetRefinementCheckboxesProps } from '@uncinc/react-searchkit/dist/components/Facets/RefinementCheckboxes';

import { SCREEN_NORMAL } from '../../../config/constants';
import SearchResetButton from '../ResetButton';
import Dialog from '../Dialog';

import './style.scss';

const SearchAllContentFacetItem: React.FC<SearchFacetRefinementCheckboxesProps> = ({
  className,
  attribute,
  ...props
}) => {
  const { t } = useTranslation();

  return (
    <div className="SearchAllContentFacetItem ">
      <label
        className="SearchAllContentFacetItem__title"
        htmlFor={`refinementList_${attribute}`}
      >
        {t(`components.search.facets.${attribute}.title`)}
      </label>
      <SearchFacetRefinementCheckboxes
        className={c('WebformWrapper', className)}
        attribute={attribute}
        transformItems={(items) => items.filter((item) => !(
          (attribute === 'combined_types' && item.label === 'article') ||
          (attribute === 'entity_type' && new Set(['node', 'taxonomy_term']).has(item.label))
        )).map((item) => ({
          ...item,
          label: t(`components.search.facets.${attribute}.${item.label}`),
        }))}
        {...props}
      />
    </div>
  );
};

const SearchAllContentFacetDialog: React.FC<React.PropsWithChildren> = ({ children, ...props }) => {
  const { t } = useTranslation();
  // Current total amount of hits from ES.
  const { nbHits: amount } = useStats();

  return (
    <Dialog
      title={t('components.search.facets.title')}
      openButtonTitle={t('components.search.facets.more')}
      ctaButtonTitle={t('components.search.facets.show_results', { amount })}
      {...props}
    >
      {children}
    </Dialog>
  );
};

const SearchAllContentFacets: React.FC<SearchFacetsProps> = (props) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const isDesktop = useMemo(() => width >= SCREEN_NORMAL, [width]);

  return useMemo(() => (
    <SearchFacets className="SearchAllContentFacets" {...props}>
      {isDesktop ? (
        <>
          <div className="SearchAllContentFacets__header">
            <h2 className="SearchAllContentFacets__header__title">
              {t('components.search.facets.title')}
            </h2>
            <SearchResetButton resetSearch={false} />
          </div>

          <div className="SearchAllContentFacets__items">
            {['combined_types', 'entity_type'].map((attribute) => (
              <SearchAllContentFacetItem attribute={attribute} />
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="SearchAllContentFacets__items">
            <SearchAllContentFacetItem
              attribute="combined_types"
              limit={6}
            />
          </div>

          <SearchAllContentFacetDialog>
            <div className="SearchAllContentFacets__items">
              {['combined_types', 'entity_type'].map((attribute) => (
                <SearchAllContentFacetItem attribute={attribute} />
              ))}
            </div>
          </SearchAllContentFacetDialog>
        </>
      )}
    </SearchFacets>
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [isDesktop]);
};

export default SearchAllContentFacets;
